import { sendEventToGTM } from "./Common.js";

// GTM Event Names - Following PRD specifications
export const GTM_EVENTS = {
  // Search Behavior
  SEARCH_BAR_CLICKED: "SEARCH_BAR_CLICKED",
  SEARCHED: "SEARCHED",
  SEARCH_RESULT_CLICKED: "SEARCH_RESULT_CLICKED",

  // Form Behaviors
  FORM_LOADED: "FORM_LOADED",
  FORM_SUBMITTED: "FORM_SUBMITTED",

  // Page Visit Behavior
  PAGE_VIEWED: "PAGE_VIEWED",

  // Behavior on Product Page
  FAQ_CLICKED: "FAQ_CLICKED",
  TDS_DOWNLOADED: "TDS_DOWNLOADED",
  MSDS_DOWNLOADED: "MSDS_DOWNLOADED",
  REQUEST_QUOTE_PDP: "REQUEST_QUOTE_PDP",
  REQUEST_SAMPLE_PDP: "REQUEST_SAMPLE_PDP",
};

// Form Types as per PRD
export const FORM_TYPES = {
  CONTACT_US: "Contact Us",
  REQUEST_SAMPLE_QUOTES: "Request Sample/Quotes",
  DOWNLOAD_TDS_MSDS: "Download TDS/MSDS Leads Form",
  GET_IN_TOUCH: "Get In Touch",
  REQUEST_CUSTOM_FORMULATION: "Request Custom Formulation",
};

// Page Types as per PRD
export const PAGE_TYPES = {
  HOME: "home",
  INDUSTRY: "industry",
  PRODUCT: "product",
  CUSTOM_MANUFACTURING: "custom manufacturing",
  OTHER: "other",
};

/**
 * Helper function to determine page type from URL pathname
 * @param {string} pathname - Current page pathname
 * @returns {string} Page type
 */
export const getPageType = (pathname) => {
  if (!pathname) return PAGE_TYPES.OTHER;

  if (pathname === "/") return PAGE_TYPES.HOME;
  if (pathname.startsWith("/product/")) return PAGE_TYPES.PRODUCT;
  if (pathname.startsWith("/industry/")) return PAGE_TYPES.INDUSTRY;
  if (
    pathname.startsWith("/custom-manufacturing") ||
    pathname.startsWith("/contract-manufacturing")
  )
    return PAGE_TYPES.CUSTOM_MANUFACTURING;

  return PAGE_TYPES.OTHER;
};

/**
 * Track search bar click event
 * @param {string} pageUrl - Current page URL
 * @param {string} pageType - Type of page (home, industry, product, other)
 */
export const trackSearchBarClicked = (pageUrl, pageType) => {
  sendEventToGTM(GTM_EVENTS.SEARCH_BAR_CLICKED, {
    page_url: pageUrl,
    page_type: pageType,
  });
};

/**
 * Track search event (debounced - push only last query)
 * @param {string} pageUrl - Current page URL
 * @param {string} pageType - Type of page (home, industry, product)
 * @param {string} query - Search query
 * @param {number} resultsReturned - Number of results returned
 */
export const trackSearched = (pageUrl, pageType, query, resultsReturned) => {
  sendEventToGTM(GTM_EVENTS.SEARCHED, {
    page_url: pageUrl,
    page_type: pageType,
    query: query,
    results_returned: resultsReturned,
  });
};

/**
 * Track search result click
 * @param {number} clickPosition - Position of clicked item in results
 * @param {string} clickedItemName - Name of clicked item
 */
export const trackSearchResultClicked = (clickPosition, clickedItemName) => {
  sendEventToGTM(GTM_EVENTS.SEARCH_RESULT_CLICKED, {
    click_position: clickPosition,
    clicked_item_name: clickedItemName,
  });
};

/**
 * Track form loaded event
 * @param {string} formType - Type of form (from FORM_TYPES)
 * @param {string} pageUrl - Current page URL
 * @param {string} pageType - Type of page (home, industry, product, custom manufacturing, other)
 */
export const trackFormLoaded = (formType, pageUrl, pageType) => {
  sendEventToGTM(GTM_EVENTS.FORM_LOADED, {
    form_type: formType,
    page_url: pageUrl,
    page_type: pageType,
  });
};

/**
 * Track form submitted event
 * @param {string} formType - Type of form (from FORM_TYPES)
 * @param {string} pageUrl - Current page URL
 * @param {string} pageType - Type of page
 */
export const trackFormSubmitted = (formType, pageUrl, pageType) => {
  sendEventToGTM(GTM_EVENTS.FORM_SUBMITTED, {
    form_type: formType,
    page_url: pageUrl,
    page_type: pageType,
  });
};

/**
 * Track page view event
 * @param {string} pageType - Type of page (home, industry, product, custom manufacturing, other)
 * @param {string} pageUrl - Current page URL
 */
export const trackPageViewed = (pageType, pageUrl) => {
  sendEventToGTM(GTM_EVENTS.PAGE_VIEWED, {
    page_type: pageType,
    page_url: pageUrl,
  });
};

/**
 * Track FAQ click event
 * @param {string} pageUrl - Current page URL
 * @param {string} productTitle - Product title
 * @param {string} faqTitle - FAQ question/title
 */
export const trackFaqClicked = (pageUrl, productTitle, faqTitle) => {
  sendEventToGTM(GTM_EVENTS.FAQ_CLICKED, {
    page_url: pageUrl,
    product_title: productTitle,
    faq_title: faqTitle,
  });
};

/**
 * Track TDS download event
 * @param {string} pageUrl - Current page URL
 * @param {string} productTitle - Product title
 */
export const trackTdsDownloaded = (pageUrl, productTitle) => {
  sendEventToGTM(GTM_EVENTS.TDS_DOWNLOADED, {
    page_url: pageUrl,
    product_title: productTitle,
  });
};

/**
 * Track MSDS download event
 * @param {string} pageUrl - Current page URL
 * @param {string} productTitle - Product title
 */
export const trackMsdsDownloaded = (pageUrl, productTitle) => {
  sendEventToGTM(GTM_EVENTS.MSDS_DOWNLOADED, {
    page_url: pageUrl,
    product_title: productTitle,
  });
};
